import { VK_URL_PARAMS } from './VkUrlParams'
import { YANDEX_URL_PARAMS } from './YandexUrlParams'

export const URL_PARAM_GROUPS = {
    general: 'General',
    ads: 'Ads',
    vk: 'VK',
    yandex: 'Yandex',
    resources: 'Resources',
} as const

export type UrlParamGroup = keyof typeof URL_PARAM_GROUPS

export interface UrlParamDefinition {
    name: string
    description: string
    group?: UrlParamGroup
}

// Rules:
// - Keep each definition in three lines: name, description, group.
// - Use backticks for examples like `param=1`.
export const URL_PARAMS: readonly UrlParamDefinition[] = [
    {
        name: 'env', //
        description: 'Selects runtime environment (example: `env=dev`).',
        group: 'general',
    },
    {
        name: 'dev', //
        description: 'Enables dev mode (example: `dev=1`).',
        group: 'general',
    },
    {
        name: 'debug', //
        description: 'Enables Phaser debug mode (example: `debug=1`).',
        group: 'general',
    },
    {
        name: 'canvas', //
        description: 'Forces canvas renderer (example: `canvas=1`).',
        group: 'general',
    },
    {
        name: 'language', //
        description: 'Sets language (example: `language=en`).',
        group: 'general',
    },
    {
        name: 'lang', //
        description: 'Sets language (short form, example: `lang=en`).',
        group: 'general',
    },
    {
        name: 'start', //
        description: 'Sets initial state/scene (example: `start=MainMenu`).',
        group: 'general',
    },
    {
        name: 'stats', //
        description: 'Shows FPS stats overlay (example: `stats=1`).',
        group: 'general',
    },
    {
        name: 'boosters', //
        description: 'Adds boosters for quick testing (example: `boosters=1`).',
        group: 'general',
    },
    {
        name: 'skipTutorial', //
        description: 'Skips tutorial flow (example: `skipTutorial=1`).',
        group: 'general',
    },
    {
        name: 'headstartPopup', //
        description: 'Forces headstart popup (example: `headstartPopup=1`).',
        group: 'general',
    },
    {
        name: 'fastGameComplete', //
        description: 'Speeds up game complete flow (example: `fastGameComplete=1`).',
        group: 'general',
    },
    {
        name: 'noAvif', //
        description: 'Disables AVIF assets (example: `noAvif=1`).',
        group: 'general',
    },
    {
        name: 'noWebp', //
        description: 'Disables WebP assets (example: `noWebp=1`).',
        group: 'general',
    },
    {
        name: 'noSidebars', //
        description: 'Removes sidebars in layout (example: `noSidebars=1`).',
        group: 'general',
    },
    {
        name: 'pokiDev', //
        description: 'Enables Poki SDK debug mode (example: `pokiDev=1`).',
        group: 'general',
    },
    {
        name: 'sitelock', //
        description: 'Ignores sitelock checks (example: `sitelock=1`).',
        group: 'general',
    },
    {
        name: 'urlParamsViewer', //
        description: 'Forces URL params viewer to open on start (example: `urlParamsViewer=1`).',
        group: 'general',
    },
    {
        name: 'minLogLevel', //
        description: 'Sets minimum log level (example: `minLogLevel=info`).',
        group: 'general',
    },
    {
        name: 'sentry', //
        description: 'Enables/disables Sentry (example: `sentry=0`).',
        group: 'general',
    },
    {
        name: 'sentryDebug', //
        description: 'Enables Sentry debug mode (example: `sentryDebug=1`).',
        group: 'general',
    },
    {
        name: 'ctl', //
        description: 'Enables GameController for debugging via `window.ctl` (example: `ctl=1`).',
        group: 'general',
    },
    {
        name: 'maxWidth', //
        description: 'Forces max width (example: `maxWidth=1024`).',
        group: 'general',
    },
    {
        name: 'mobileScale', //
        description: 'Forces mobile scale mode (example: `mobileScale=1`).',
        group: 'general',
    },
    {
        name: 'coins', //
        description: 'Sets coins amount (example: `coins=1000`).',
        group: 'resources',
    },
    {
        name: 'magnet', //
        description: 'Sets "Magnet" booster amount (example: `magnet=5`).',
        group: 'resources',
    },
    {
        name: 'undo', //
        description: 'Sets "Undo" booster amount (example: `undo=10`).',
        group: 'resources',
    },
    {
        name: 'remove', //
        description: 'Sets "Remove" booster amount (example: `remove=99`).',
        group: 'resources',
    },
    {
        name: 'sort', //
        description: 'Sets "Sort" booster amount (example: `sort=2`).',
        group: 'resources',
    },
    {
        name: 'upgrade', //
        description: 'Sets "Upgrade" booster amount (example: `upgrade=3`).',
        group: 'resources',
    },
    {
        name: 'rewardedAds', //
        description: 'Toggles rewarded ads (example: `rewardedAds=0`).',
        group: 'ads',
    },
    {
        name: 'interAds', //
        description: 'Toggles interstitial ads (example: `interAds=0`).',
        group: 'ads',
    },
    {
        name: 'bannerAds', //
        description: 'Toggles banner ads (example: `bannerAds=0`).',
        group: 'ads',
    },
    ...VK_URL_PARAMS,
    ...YANDEX_URL_PARAMS,
] as const

export type UrlParam = (typeof URL_PARAMS)[number]['name']


export class UrlParams {
    private static usp: URLSearchParams & { originalSearch?: string; originalHash?: string }

    private static init() {
        let search = window.location.search
        let hash = window.location.hash

        if (!this.usp || this.usp.originalSearch !== search || this.usp.originalHash !== hash) {
            this.usp = new URLSearchParams(search)

            const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
            hashParams.forEach((value, key) => {
                if (!this.usp.has(key)) {
                    this.usp.set(key, value)
                }
            })

            this.usp.originalSearch = search
            this.usp.originalHash = hash
        }

        return this.usp
    }

    public static get(name: UrlParam): string | null {
        return this.init().get(name)
    }

    public static getAll(): Record<string, string | null> {
        const result: Record<string, string | null> = {}

        this.init().forEach((value, key) => {
            result[key] = value
        })

        return result
    }

    public static has(name: UrlParam): boolean {
        return this.get(name) !== null
    }

    public static getNumber(name: UrlParam, _default = 0): number {
        let value = this.get(name)
        if (value) {
            let num = parseFloat(value)
            return isNaN(num) ? _default : num
        }

        return _default
    }

    public static getNumbers(name: UrlParam, _default?: number[]): number[] | null {
        let value = this.get(name)
        if (!value) {
            return _default ?? null
        }

        if (value[0] !== '[' || value[value.length - 1] !== ']') {
            console.warn('Malformed url param input', value)
            return null
        }

        let content = value.slice(1, value.length - 1)
        return content.split(',').map((numStr) => {
            return parseFloat(numStr)
        })
    }

    public static getBool(name: UrlParam, expectedValue = '1'): boolean {
        let value = this.get(name)

        return !!(value && value === expectedValue)
    }
}
